import { useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { ApolloError } from "@apollo/client";
import { Select } from "@normasteaching/norma-ui";
import { Layout } from "../UI/Layout/Layout.tsx";
import { Card } from "../UI/Card.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { SegmentedControl } from "../UI/SegmentedControl.tsx";
import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { useToast } from "../UI/Feedback/useToast.ts";
import {
  OnboardingLiveCreationIntentStatus,
  OnboardingLiveListPeriod,
} from "../gql/graphql.ts";
import { useAllUserGroups } from "../UserGroup/useAllUserGroups.ts";
import { useOnboardingLives } from "./useOnboardingLives.ts";
import { useOnboardingLiveCreationIntents } from "./useOnboardingLiveCreationIntents.ts";
import { useOnboardingLiveConductors } from "./useOnboardingLiveConductors.ts";
import {
  onboardingLiveDetailPathOf,
  onboardingLivePlanPathOf,
} from "./OnboardingLivePaths.ts";

enum OnboardingLivesTab {
  Lives = "Lives",
  Plans = "Plans",
}

const intentStatusLabels: Record<OnboardingLiveCreationIntentStatus, string> = {
  [OnboardingLiveCreationIntentStatus.Draft]: "Bozza",
  [OnboardingLiveCreationIntentStatus.Confirmed]: "Creazione in corso",
  [OnboardingLiveCreationIntentStatus.Completed]: "Completata",
  [OnboardingLiveCreationIntentStatus.Cancelled]: "Annullata",
};

function formatDateTime(date?: string | null): string {
  return date
    ? dayjs(date).tz("Europe/Rome").format("ddd DD/MM/YYYY HH:mm")
    : "-";
}

function useUserGroupNames() {
  const { userGroups } = useAllUserGroups();
  return {
    userGroups,
    userGroupNameOf: (userGroupId: string) =>
      userGroups.find((g) => g.id === userGroupId)?.name ?? userGroupId,
  };
}

function OnboardingLivesList() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(OnboardingLiveListPeriod.Upcoming);
  const [userGroupId, setUserGroupId] = useState<string>();
  const { onboardingLives, loading } = useOnboardingLives({
    period,
    userGroupId,
  });
  const { userGroups, userGroupNameOf } = useUserGroupNames();
  const { teacherNameOf } = useOnboardingLiveConductors();

  return (
    <Card>
      <div className={"flex flex-col md:flex-row md:items-center gap-3 mb-4"}>
        <SegmentedControl
          options={[
            { value: OnboardingLiveListPeriod.Upcoming, label: "Future" },
            { value: OnboardingLiveListPeriod.Past, label: "Già svolte" },
          ]}
          value={period}
          onChange={setPeriod}
        />
        <div className={"max-w-xs w-full"}>
          <Select<string>
            placeholder={"Tutti gli user group"}
            options={userGroups
              .filter((g) => g.onboardingLiveRequired)
              .map((g) => ({ id: g.id, name: g.name }))}
            value={userGroupId}
            onChange={(v) => setUserGroupId(v ?? undefined)}
            clearable
          />
        </div>
      </div>
      {loading && onboardingLives.length <= 0 ? (
        <LoadingRows />
      ) : onboardingLives.length <= 0 ? (
        <p className={"text-sm text-slate-500"}>Nessuna live onboarding.</p>
      ) : (
        <div className={"overflow-x-auto"}>
          <table className={"w-full text-sm"}>
            <thead>
              <tr className={"text-left text-slate-500 border-b"}>
                <th className={"py-2 pr-4"}>Data e ora</th>
                <th className={"py-2 pr-4"}>User group</th>
                <th className={"py-2 pr-4"}>Conduttore</th>
                <th className={"py-2 pr-4"}>Iscritti</th>
                {period === OnboardingLiveListPeriod.Past ? (
                  <>
                    <th className={"py-2 pr-4"}>Partecipanti (feedback)</th>
                    <th className={"py-2 pr-4"}>Feedback</th>
                  </>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {onboardingLives.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => navigate(onboardingLiveDetailPathOf(l.id))}
                  className={"border-b cursor-pointer hover:bg-slate-50"}
                >
                  <td className={"py-2 pr-4 font-medium"}>
                    {formatDateTime(l.startUtc)}
                  </td>
                  <td className={"py-2 pr-4"}>
                    {userGroupNameOf(l.userGroupId)}
                  </td>
                  <td className={"py-2 pr-4"}>{teacherNameOf(l.teacherId)}</td>
                  <td className={"py-2 pr-4"}>
                    {l.participantsCount} / {l.capacity}
                  </td>
                  {period === OnboardingLiveListPeriod.Past ? (
                    <>
                      <td className={"py-2 pr-4"}>
                        {l.feedback?.participantsCount ?? "-"}
                      </td>
                      <td className={"py-2 pr-4"}>
                        {l.feedback ? (
                          <span className={"text-emerald-700"}>Inviato</span>
                        ) : (
                          <span className={"text-amber-700"}>Mancante</span>
                        )}
                      </td>
                    </>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function OnboardingLivePlansList() {
  const navigate = useNavigate();
  const showToast = useToast();
  const { intents, loading, createIntent, creating } =
    useOnboardingLiveCreationIntents();
  const { userGroups, userGroupNameOf } = useUserGroupNames();
  const groupsWithOnboardingLive = userGroups.filter(
    (g) => g.onboardingLiveRequired,
  );
  const defaultGroupId =
    groupsWithOnboardingLive.find((g) => g.id === "adults")?.id ??
    groupsWithOnboardingLive[0]?.id;
  const [selectedGroupId, setSelectedGroupId] = useState<string>();
  const userGroupId = selectedGroupId ?? defaultGroupId;

  async function newPlan() {
    if (!userGroupId) return;
    try {
      const intentId = await createIntent(userGroupId);
      navigate(onboardingLivePlanPathOf(intentId));
    } catch (e) {
      showToast((e as ApolloError)?.message ?? "Errore", "error");
    }
  }

  return (
    <div className={"space-y-4"}>
      <Card>
        {groupsWithOnboardingLive.length <= 0 ? (
          <p className={"text-sm text-slate-600"}>
            Nessuno user group prevede le live onboarding: abilitale dalla
            pagina User Groups per poterle creare.
          </p>
        ) : (
          <div className={"flex flex-col md:flex-row md:items-end gap-3"}>
            <div className={"max-w-xs w-full"}>
              <p className={"text-sm font-semibold mb-1"}>User group</p>
              <Select<string>
                options={groupsWithOnboardingLive.map((g) => ({
                  id: g.id,
                  name: g.name,
                }))}
                value={userGroupId}
                onChange={(v) => setSelectedGroupId(v ?? undefined)}
              />
            </div>
            <PrimaryButton
              label={"Nuova pianificazione"}
              loading={creating}
              onClick={newPlan}
            />
          </div>
        )}
      </Card>
      <Card>
        {loading && intents.length <= 0 ? (
          <LoadingRows />
        ) : intents.length <= 0 ? (
          <p className={"text-sm text-slate-500"}>Nessuna pianificazione.</p>
        ) : (
          <div className={"overflow-x-auto"}>
            <table className={"w-full text-sm"}>
              <thead>
                <tr className={"text-left text-slate-500 border-b"}>
                  <th className={"py-2 pr-4"}>Creata il</th>
                  <th className={"py-2 pr-4"}>Creata da</th>
                  <th className={"py-2 pr-4"}>User group</th>
                  <th className={"py-2 pr-4"}>Slot</th>
                  <th className={"py-2 pr-4"}>Stato</th>
                </tr>
              </thead>
              <tbody>
                {intents.map((i) => (
                  <tr
                    key={i.id}
                    onClick={() => navigate(onboardingLivePlanPathOf(i.id))}
                    className={"border-b cursor-pointer hover:bg-slate-50"}
                  >
                    <td className={"py-2 pr-4"}>
                      {formatDateTime(i.createdAtUtc)}
                    </td>
                    <td className={"py-2 pr-4"}>{i.createdByEmail ?? "-"}</td>
                    <td className={"py-2 pr-4"}>
                      {userGroupNameOf(i.userGroupId)}
                    </td>
                    <td className={"py-2 pr-4"}>{i.slots.length}</td>
                    <td className={"py-2 pr-4"}>
                      {intentStatusLabels[i.status]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export function OnboardingLivesScreen() {
  const [tab, setTab] = useState(OnboardingLivesTab.Lives);
  return (
    <Layout>
      <Card>
        <div
          className={
            "flex flex-col md:flex-row md:items-center md:justify-between gap-3"
          }
        >
          <ScreenTitleBlock title={"Live onboarding"} />
          <SegmentedControl
            options={[
              { value: OnboardingLivesTab.Lives, label: "Live" },
              { value: OnboardingLivesTab.Plans, label: "Pianificazioni" },
            ]}
            value={tab}
            onChange={setTab}
          />
        </div>
      </Card>
      <div className={"mt-4"}>
        {tab === OnboardingLivesTab.Lives ? (
          <OnboardingLivesList />
        ) : (
          <OnboardingLivePlansList />
        )}
      </div>
    </Layout>
  );
}
